<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useSidePanel, useDrawer, Page, useSecondaryNavigationPanel } from '@dashhudson/dashing-ui';
import { useFlagStore } from '@/stores/flag';
import { useCropperStore } from '@/stores/cropper';
import PrimaryNavigation from '@/components/layout/navigation/PrimaryNavigation.vue';
import SidePanelProvider from '@/components/dashingUI/SidePanelProvider.vue';
import DrawerProvider from '@/components/dashingUI/DrawerProvider.vue';
import { useTopBannerOffsetManager } from '@/composables/layout/useTopBannerOffsetManager';

const route = useRoute();

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  showNav: {
    type: Boolean,
    default: true,
  },
});

const flagStore = useFlagStore();
const cropperStore = useCropperStore();

const { visible: sidePanelVisible } = useSidePanel();
const { visible } = useDrawer();
const { hidden, expanded } = useSecondaryNavigationPanel();

const filterSidePanelTransitionClasses = ref('-z-10 hidden');
const contentAreaTransitionClasses = ref('');
const dashingLayoutWrapperClasses = ref('');

const isDrawerOpen = computed(() => !!visible.value);
const showPageOverlay = computed(() => isDrawerOpen.value);

const secondaryNavHidden = computed(() => hidden.value);
const secondaryNavExpanded = computed(() => {
  const hasAccessToSecondaryNavViaFeatureFlag =
    flagStore.ready && flagStore.flags?.[route?.meta?.showSecondaryNavFeatureFlagOverride];

  if (route?.meta?.showSecondaryNavFeatureFlagOverride)
    return expanded.value && hasAccessToSecondaryNavViaFeatureFlag;
  return expanded.value && !route?.meta?.hideSecondaryNav;
});

const useReportLayout = computed(() => route?.meta?.report);

useTopBannerOffsetManager({ enabled: computed(() => !useReportLayout.value) });

const hidePrimaryNav = computed(() => route?.meta?.hidePrimaryNav);

const secondaryNavigationVisible = computed(
  () => !secondaryNavHidden.value && !useReportLayout.value && secondaryNavExpanded.value,
);

function handleContentTransitionStart($event) {
  const { propertyName, target } = $event;
  if (propertyName === 'transform' && target.id === 'mainContentArea') {
    filterSidePanelTransitionClasses.value = '-z-10 block';
    contentAreaTransitionClasses.value = 'z-10 dash-bg-background-page';
  }
}

function handleContentTransitionEnd($event) {
  const { propertyName, target } = $event;
  if (propertyName === 'transform' && target.id === 'mainContentArea') {
    if (sidePanelVisible.value) {
      filterSidePanelTransitionClasses.value = '!z-20 !block';
      contentAreaTransitionClasses.value = 'z-10 dash-bg-background-page';
      dashingLayoutWrapperClasses.value = 'before:z-[1]';
    } else {
      filterSidePanelTransitionClasses.value = '-z-10 hidden';
      contentAreaTransitionClasses.value = '';
      dashingLayoutWrapperClasses.value = '';
    }
  }
}

const primaryNavigationTranslateX = computed(() => {
  if (sidePanelVisible.value) {
    if (secondaryNavigationVisible.value) {
      return '-translate-x-[var(--combined-navigation-width)]';
    }
    return '-translate-x-[var(--primary-navigation-width)]';
  }
  return '';
});

const backgroundTranslateX = computed(() => {
  if (sidePanelVisible.value) {
    return 'before:!-translate-x-[var(--combined-navigation-width)]';
  }
  return '';
});

const mainContentAreaTranslateX = computed(() => {
  if (sidePanelVisible.value) {
    return '-translate-x-[var(--combined-navigation-width)]';
  }
  return '';
});

const pageClasses = computed(() => {
  const classes = [contentAreaTransitionClasses.value, mainContentAreaTranslateX.value];

  if (useReportLayout.value || hidePrimaryNav.value) {
    classes.push('ml-0', 'w-full');
  } else if (sidePanelVisible.value) {
    classes.push(
      'ml-[var(--combined-navigation-width)]',
      'w-[calc(100%-var(--combined-navigation-width))]',
    );
  } else if (secondaryNavigationVisible.value) {
    classes.push(
      '!ml-[var(--primary-navigation-width)] xl:!ml-[var(--combined-navigation-width)]',
      'w-[calc(100%-var(--primary-navigation-width))] xl:w-[calc(100%-var(--combined-navigation-width))]',
    );
  } else {
    classes.push(
      'ml-[var(--primary-navigation-width)]',
      'w-[calc(100%-var(--primary-navigation-width))]',
    );
  }

  return classes;
});

const innerPadding = computed(() => {
  if (!useReportLayout.value && !secondaryNavHidden.value && !hidePrimaryNav.value) {
    return '!px-10';
  }
  return null;
});

const isPrintLayout = computed(() => route.meta?.report || route.meta?.isSalesforceEmbed);

const roundTopLeft = computed(() => {
  if (route?.meta?.hideSecondaryNav) {
    return true;
  }
  return expanded.value !== true;
});
</script>

<template>
  <div
    class="!relative"
    :class="[backgroundTranslateX, dashingLayoutWrapperClasses]"
    data-dashing-layout-wrapper
  >
    <PrimaryNavigation
      v-if="props.showNav"
      :hide-toggle-button="sidePanelVisible"
      class="dash-transition-md"
      :class="primaryNavigationTranslateX"
    />
    <Page
      id="mainContentArea"
      data-main-content-area
      :show-overlay="showPageOverlay"
      :class="pageClasses"
      :print="isPrintLayout"
      :rounded="roundTopLeft"
      @transitionstart="handleContentTransitionStart"
      @transitionend="handleContentTransitionEnd"
    >
      <div :class="innerPadding">
        <slot />
      </div>
    </Page>
    <SidePanelProvider
      :class="filterSidePanelTransitionClasses"
      :disable-click-outside="cropperStore.isDragging"
    />
    <DrawerProvider
      :class="[sidePanelVisible ? 'dash-elevation-2xl' : '', mainContentAreaTranslateX]"
      @transitionstart="handleContentTransitionStart"
      @transitionend="handleContentTransitionEnd"
    />
  </div>
</template>

<style lang="postcss" scoped>
.background-transition::before {
  transition: all 0.3s cubic-bezier(0.35, 0, 0.3, 1.1);
}
</style>
<style lang="postcss">
[data-dashing-layout-wrapper]::before {
  top: var(--dash-top-banner-offset, 0) !important;
}

[data-dash-page-header] {
  padding-top: calc(var(--dash-top-banner-offset, 0) - 1px) !important;
}
</style>
